import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeLatest } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";

export type CheckAgencyUsernameLoadAction = {
  type: string;
  payload: { username: string };
};

export type CheckAgencyUsernameData = {
  data: {
    available: boolean;
    username: string;
  };
};

const CheckAgencyUsername = new ReduxFetchState<
  CheckAgencyUsernameData | null,
  { username: string },
  any
>("checkAgencyUsername");

const RESET_CHECK_AGENCY_USERNAME = "RESET_CHECK_AGENCY_USERNAME";

export function* watchCheckAgencyUsername(action: CheckAgencyUsernameLoadAction) {
  const { username } = action.payload;
  try {
    const lang: "fa" | "en" = yield getCurrentLanguage();

    const data: { data: { available: boolean } } = yield axios({
      url: "/api/v1/ai/agencies/username/check",
      method: "POST",
      headers: { lang },
      data: { username },
    });

    yield put(
      CheckAgencyUsername.actions.loadSuccess({
        data: { available: !!data.data.available, username },
      }),
    );
  } catch (e: any) {
    // No toast here: this runs while the user is still typing. When the check
    // fails the form simply does not block the user (the final submit validates again).
    yield put(CheckAgencyUsername.actions.loadFailure(e.message));
  }
}

export function* checkAgencyUsernameSagas() {
  // takeLatest: only the answer for the most recently typed username matters.
  yield takeLatest(CheckAgencyUsername.actionTypes.load, watchCheckAgencyUsername);
}

const initialState = CheckAgencyUsername.reducer(undefined, { type: "@@INIT" });

const customReducer = (state = initialState, action: any) => {
  if (action.type === RESET_CHECK_AGENCY_USERNAME) {
    return initialState;
  }
  return CheckAgencyUsername.reducer(state, action);
};

export function useCheckAgencyUsername() {
  const { loading, data, ...checkAgencyUsernameState } = useAppSelector(
    (state) => state.checkAgencyUsername,
  );
  const dispatch = useAppDispatch();

  const dispatchCheckAgencyUsername = useCallback(
    (payload: { username: string }) => {
      dispatch(CheckAgencyUsername.actions.load(payload));
    },
    [dispatch],
  );

  const clearCheckAgencyUsername = useCallback(() => {
    dispatch({ type: RESET_CHECK_AGENCY_USERNAME });
  }, [dispatch]);

  return {
    ...checkAgencyUsernameState,
    dispatchCheckAgencyUsername,
    clearCheckAgencyUsername,
    checkAgencyUsernameLoading: loading,
    checkAgencyUsernameData: data,
  };
}

export const {
  actionTypes: checkAgencyUsernameActionsTypes,
  actions: checkAgencyUsernameActions,
} = CheckAgencyUsername;

export const checkAgencyUsernameReducer = customReducer;
